import { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

// ============================================
// ANIMATED TERMINAL
// ============================================
const terminalLines = [
  { cmd: '$ whoami', output: 'abhishek_kumar', delay: 0 },
  { cmd: '$ cat role.txt', output: 'Senior Software Engineer', delay: 2000 },
  { cmd: '$ echo $PASSION', output: '"Building scalable systems"', delay: 4000 },
];

function AnimatedTerminal() {
  const [displayedLines, setDisplayedLines] = useState<{ cmd: string; output: string; showOutput: boolean }[]>([]);
  const [currentTyping, setCurrentTyping] = useState('');
  const [lineIndex, setLineIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);
    return () => clearInterval(cursorInterval);
  }, []);

  useEffect(() => {
    if (lineIndex >= terminalLines.length) return;

    const currentLine = terminalLines[lineIndex];

    if (charIndex < currentLine.cmd.length) {
      const timeout = setTimeout(() => {
        setCurrentTyping((prev) => prev + currentLine.cmd[charIndex]);
        setCharIndex((prev) => prev + 1);
      }, 50 + Math.random() * 50);
      return () => clearTimeout(timeout);
    } else {
      // Command finished typing, show output after delay
      const timeout = setTimeout(() => {
        setDisplayedLines((prev) => [
          ...prev,
          { cmd: currentLine.cmd, output: currentLine.output, showOutput: true },
        ]);
        setCurrentTyping('');
        setCharIndex(0);
        setLineIndex((prev) => prev + 1);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [lineIndex, charIndex]);

  // Restart animation after all lines complete
  useEffect(() => {
    if (lineIndex >= terminalLines.length) {
      const timeout = setTimeout(() => {
        setDisplayedLines([]);
        setLineIndex(0);
        setCharIndex(0);
        setCurrentTyping('');
      }, 4000);
      return () => clearTimeout(timeout);
    }
  }, [lineIndex]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, rotateX: -10 }}
      animate={{ opacity: 1, y: 0, rotateX: 0 }}
      transition={{ duration: 0.8, delay: 0.5 }}
    >
      <Box
        sx={{
          background: 'rgba(10, 10, 15, 0.8)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(68, 212, 130, 0.2)',
          borderRadius: '16px',
          overflow: 'hidden',
          width: '100%',
          maxWidth: '400px',
        }}
      >
        {/* Terminal Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '12px 16px',
            background: 'rgba(255, 255, 255, 0.03)',
            borderBottom: '1px solid rgba(68, 212, 130, 0.1)',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: '#FF5F56' }} />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: '#FFBD2E' }} />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: '#27CA40' }} />
          </Box>
          <Typography
            sx={{
              fontFamily: 'Fira Code, monospace',
              fontSize: '0.7rem',
              color: 'rgba(255, 255, 255, 0.4)',
              ml: 2,
            }}
          >
            ~/abhishek
          </Typography>
        </Box>

        {/* Terminal Body */}
        <Box sx={{ padding: '16px', minHeight: '140px' }}>
          {displayedLines.map((line, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Fira Code, monospace',
                  fontSize: '0.8rem',
                  color: 'rgba(255, 255, 255, 0.7)',
                }}
              >
                {line.cmd}
              </Typography>
              {line.showOutput && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Fira Code, monospace',
                      fontSize: '0.8rem',
                      color: '#44D482',
                      pl: 2,
                    }}
                  >
                    {line.output}
                  </Typography>
                </motion.div>
              )}
            </Box>
          ))}
          {lineIndex < terminalLines.length && (
            <Typography
              sx={{
                fontFamily: 'Fira Code, monospace',
                fontSize: '0.8rem',
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              {currentTyping}
              <span style={{ color: '#44D482', opacity: showCursor ? 1 : 0 }}>▋</span>
            </Typography>
          )}
        </Box>
      </Box>
    </motion.div>
  );
}

// ============================================
// 3D CODE CARD
// ============================================
const codeSnippet = `const developer = {
  name: "Abhishek",
  skills: ["React", "Node"],
  coffee: Infinity,
  build: () => "🚀"
};`;

function CodeCard3D() {
  const cardRef = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [15, -15]), { damping: 20, stiffness: 300 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-15, 15]), { damping: 20, stiffness: 300 });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.8 }}
      style={{ perspective: 1000 }}
    >
      <motion.div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d',
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(68, 212, 130, 0.1) 0%, rgba(68, 212, 130, 0.05) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(68, 212, 130, 0.2)',
            borderRadius: '16px',
            padding: '20px',
            width: '100%',
            maxWidth: '280px',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'pointer',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #44D482, transparent)',
            },
          }}
        >
          {/* Shine effect */}
          <Box
            sx={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'linear-gradient(45deg, transparent 40%, rgba(68, 212, 130, 0.1) 50%, transparent 60%)',
              transform: 'translateZ(50px)',
              pointerEvents: 'none',
            }}
          />
          
          <Typography
            sx={{
              fontFamily: 'Fira Code, monospace',
              fontSize: '0.65rem',
              color: 'rgba(255, 255, 255, 0.4)',
              mb: 1,
            }}
          >
            developer.js
          </Typography>
          <pre
            style={{
              fontFamily: 'Fira Code, monospace',
              fontSize: '0.7rem',
              color: 'rgba(255, 255, 255, 0.8)',
              margin: 0,
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6,
            }}
          >
            <span style={{ color: '#44D482' }}>const</span>{' '}
            <span style={{ color: '#fff' }}>developer</span> = {'{'}
            {'\n'}  <span style={{ color: '#44D482' }}>name</span>:{' '}
            <span style={{ color: '#F5DE19' }}>"Abhishek"</span>,
            {'\n'}  <span style={{ color: '#44D482' }}>skills</span>:{' '}
            <span style={{ color: '#61DAFB' }}>["React", "Node"]</span>,
            {'\n'}  <span style={{ color: '#44D482' }}>coffee</span>:{' '}
            <span style={{ color: '#E0234E' }}>Infinity</span>,
            {'\n'}  <span style={{ color: '#44D482' }}>build</span>:{' '}
            <span style={{ color: '#fff' }}>() =&gt; "🚀"</span>
            {'\n'}{'}'}
          </pre>
        </Box>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// INTERACTIVE CIRCUIT
// ============================================
function InteractiveCircuit() {
  const [activeNodes, setActiveNodes] = useState<number[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      const randomNodes = Array.from({ length: 3 }, () => Math.floor(Math.random() * 9));
      setActiveNodes(randomNodes);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  const nodes = [
    { x: 20, y: 20 },
    { x: 80, y: 15 },
    { x: 140, y: 25 },
    { x: 30, y: 70 },
    { x: 90, y: 65 },
    { x: 150, y: 75 },
    { x: 25, y: 120 },
    { x: 85, y: 115 },
    { x: 145, y: 125 },
  ];

  const connections = [
    [0, 1], [1, 2], [0, 3], [1, 4], [2, 5],
    [3, 4], [4, 5], [3, 6], [4, 7], [5, 8],
    [6, 7], [7, 8], [0, 4], [1, 5], [3, 7], [4, 8],
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: 1.1 }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '170px',
          height: '150px',
        }}
      >
        <svg width="170" height="150" style={{ position: 'absolute', top: 0, left: 0 }}>
          {/* Connections */}
          {connections.map(([from, to], index) => {
            const isActive = activeNodes.includes(from) || activeNodes.includes(to);
            return (
              <motion.line
                key={index}
                x1={nodes[from].x}
                y1={nodes[from].y}
                x2={nodes[to].x}
                y2={nodes[to].y}
                stroke={isActive ? '#44D482' : 'rgba(68, 212, 130, 0.2)'}
                strokeWidth={isActive ? 2 : 1}
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1, delay: index * 0.05 }}
              />
            );
          })}

          {/* Nodes */}
          {nodes.map((node, index) => {
            const isActive = activeNodes.includes(index);
            return (
              <motion.g key={index}>
                {/* Glow effect */}
                {isActive && (
                  <motion.circle
                    cx={node.x}
                    cy={node.y}
                    r={12}
                    fill="none"
                    stroke="#44D482"
                    strokeWidth={1}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: [0, 0.5, 0], scale: [0.5, 1.5, 2] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                )}
                <motion.circle
                  cx={node.x}
                  cy={node.y}
                  r={isActive ? 6 : 4}
                  fill={isActive ? '#44D482' : 'rgba(68, 212, 130, 0.4)'}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                  style={{
                    filter: isActive ? 'drop-shadow(0 0 8px #44D482)' : 'none',
                  }}
                />
              </motion.g>
            );
          })}
        </svg>

        {/* Label */}
        <Typography
          sx={{
            position: 'absolute',
            bottom: -10,
            left: '50%',
            transform: 'translateX(-50%)',
            fontFamily: 'Fira Code, monospace',
            fontSize: '0.65rem',
            color: 'rgba(255, 255, 255, 0.4)',
            whiteSpace: 'nowrap',
          }}
        >
          network.active
        </Typography>
      </Box>
    </motion.div>
  );
}

// ============================================
// MAIN HERO VISUALS COMPONENT
// ============================================
export default function HeroVisuals() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        alignItems: 'flex-end',
      }}
    >
      <AnimatedTerminal />
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        <CodeCard3D />
        <InteractiveCircuit />
      </Box>
    </Box>
  );
}

